import { useState, useRef, useEffect, useCallback } from "react";
import { ZoomIn, X, ChevronLeft, ChevronRight } from "lucide-react";
import { SHOP } from "@/constants/testIds";

const COLOR_IMAGES = {
  Black: { front: "/hoodie/front/hoodie-black-front", back: "/hoodie/back/hoodie-black-back" },
  Blue: { front: "/hoodie/front/hoodie-blue-front", back: "/hoodie/back/hoodie-blue-back" },
  White: { front: "/hoodie/front/hoodie-white-front", back: "/hoodie/back/hoodie-white-back" },
  Red: { front: "/hoodie/front/hoodie-red-front", back: "/hoodie/back/hoodie-red-back" },
  Pink: { front: "/hoodie/front/hoodie-pink-front", back: "/hoodie/back/hoodie-pink-back" },
};

const EXTRA_SLIDES = [
  { key: "lineup", src: "/hoodie/gallery/hoodie-collection-lineup", w: 2038, h: 772, alt: "Pickle Wall Performance Hoodie collection in black, blue, white, red and pink" },
  { key: "detail-1", src: "/hoodie/gallery/hoodie-detail-black-blue-white-red", w: 1254, h: 1254, alt: "Pickle Wall Performance Hoodie detail board showing logo, hood tape, sleeve patch, cuffs and fleece texture in black, blue, white and red" },
  { key: "detail-2", src: "/hoodie/gallery/hoodie-detail-pink-white", w: 1254, h: 1254, alt: "Pickle Wall Performance Hoodie detail board showing logo, hood tape, sleeve patch, cuffs and fleece texture in pink and white" },
];

const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function HoodieGallery({ color, onView }) {
  const [view, setView] = useState("front"); // "front" | "back"
  const [slideIndex, setSlideIndex] = useState(0); // 0=front,1=back,2=lineup,3=detail1,4=detail2
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [fading, setFading] = useState(false);
  const touchStartX = useRef(null);
  const zoomBtnRef = useRef(null);

  const colorImgs = COLOR_IMAGES[color] || COLOR_IMAGES.White;
  const slides = [
    { key: "front", src: colorImgs.front, w: 1254, h: 1254, alt: `${color} Pickle Wall Performance Hoodie front design mockup` },
    { key: "back", src: colorImgs.back, w: 1254, h: 1254, alt: `${color} Pickle Wall Performance Hoodie back design mockup` },
    ...EXTRA_SLIDES,
  ];

  useEffect(() => {
    setSlideIndex(0);
    setView("front");
  }, [color]);

  const goTo = useCallback((idx) => {
    const next = ((idx % slides.length) + slides.length) % slides.length;
    if (reduceMotion) {
      setSlideIndex(next);
    } else {
      setFading(true);
      window.setTimeout(() => {
        setSlideIndex(next);
        setFading(false);
      }, 90);
    }
    setView(next === 1 ? "back" : "front");
    onView?.(next === 1 ? "back" : "front");
  }, [slides.length, onView]);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    zoomBtnRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowRight") goTo(slideIndex + 1);
    if (e.key === "ArrowLeft") goTo(slideIndex - 1);
    if (e.key === "Escape") closeLightbox();
  }, [goTo, slideIndex, closeLightbox]);

  const current = slides[slideIndex];

  return (
    <div data-testid="hoodie-gallery">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 mb-2">Design Mockup</div>

      <div
        className="relative aspect-[4/5] w-full bg-[#111] border border-white/10 rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]"
        tabIndex={0}
        role="group"
        aria-label={`${color} Pickle Wall Performance Hoodie gallery`}
        onKeyDown={handleKeyDown}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const delta = e.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(delta) > 40) goTo(slideIndex + (delta < 0 ? 1 : -1));
          touchStartX.current = null;
        }}
      >
        <picture>
          <source srcSet={`${current.src}.webp`} type="image/webp" />
          <img
            data-testid={SHOP.galleryMain}
            src={`${current.src}.png`}
            width={current.w}
            height={current.h}
            alt={current.alt}
            className={`w-full h-full object-contain transition-opacity duration-200 ${fading ? "opacity-0" : "opacity-100"}`}
            loading={slideIndex === 0 ? "eager" : "lazy"}
            decoding="async"
          />
        </picture>
        <button
          type="button"
          ref={zoomBtnRef}
          data-testid={SHOP.galleryZoom}
          onClick={() => setLightboxOpen(true)}
          aria-label="Zoom product image"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 border border-white/15 flex items-center justify-center hover:bg-black/80"
        >
          <ZoomIn size={16} />
        </button>
        <button
          type="button"
          onClick={() => goTo(slideIndex - 1)}
          aria-label="Previous image"
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 border border-white/10 hidden sm:flex items-center justify-center hover:bg-black/80"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => goTo(slideIndex + 1)}
          aria-label="Next image"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 border border-white/10 hidden sm:flex items-center justify-center hover:bg-black/80"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
        {slides.map((s, i) => (
          <button
            key={s.key}
            type="button"
            data-testid={SHOP.galleryThumb(s.key)}
            onClick={() => goTo(i)}
            aria-label={`View ${s.key} image`}
            aria-current={i === slideIndex}
            className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all focus-visible:ring-2 focus-visible:ring-[#FF6B00] ${
              i === slideIndex ? "border-[#FF6B00]" : "border-white/10 hover:border-white/30"
            }`}
          >
            <picture>
              <source srcSet={`${s.src}.webp`} type="image/webp" />
              <img src={`${s.src}.png`} alt="" width={s.w} height={s.h} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </picture>
          </button>
        ))}
      </div>

      {lightboxOpen && (
        <div
          data-testid={SHOP.galleryLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image zoom"
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6"
          onKeyDown={handleKeyDown}
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close zoom"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
          >
            <X size={18} />
          </button>
          <picture onClick={(e) => e.stopPropagation()}>
            <source srcSet={`${current.src}.webp`} type="image/webp" />
            <img
              src={`${current.src}.png`}
              alt={current.alt}
              className="max-w-full max-h-full object-contain"
            />
          </picture>
        </div>
      )}
    </div>
  );
}
